import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ruler, Check, AlertCircle, ChevronDown, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import type { SizingChart } from '../../types';
import { MeasuringGuideModal } from './MeasuringGuideModal';

const STORAGE_KEY = 'ndms-leg-measurements';

type Range = { min: number; max: number };

function canonicalKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/circumference/g, '')
    .replace(/\(cm\)/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// Short input suffix for a sizing-chart row unit. EU must be tested before the
// generic shoe/US test — "EU shoe size" also matches /shoe/i.
function unitSuffix(unit?: string): string {
  if (!unit) return 'cm';
  if (/eu/i.test(unit)) return 'EU';
  if (/us|shoe/i.test(unit)) return 'US';
  if (/inch/i.test(unit)) return 'in';
  return unit;
}

function parseRange(s: string): Range | null {
  const cleaned = s.trim().replace(/\s*(cm|in(?:ches)?)\s*$/i, '').replace(/\s+/g, '');
  // "18-21.5" or "18–21.5"
  const rangeMatch = cleaned.match(/^(\d+(?:\.\d+)?)[–-](\d+(?:\.\d+)?)$/);
  if (rangeMatch) return { min: parseFloat(rangeMatch[1]), max: parseFloat(rangeMatch[2]) };
  // "≤X" or "<X"
  const leMatch = cleaned.match(/^[≤<](\d+(?:\.\d+)?)$/);
  if (leMatch) return { min: 0, max: parseFloat(leMatch[1]) };
  // "≥X" or ">X"
  const geMatch = cleaned.match(/^[≥>](\d+(?:\.\d+)?)$/);
  if (geMatch) return { min: parseFloat(geMatch[1]), max: 999 };
  return null;
}

const inRange = (value: number, range: Range) => value >= range.min && value <= range.max;

function loadMeasurements(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveMeasurements(m: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
  } catch {
    // ignore quota / private-mode errors
  }
}

interface Props {
  sizingChart: SizingChart;
  availableSizes: string[];
  onSelectSize: (size: string) => void;
  selectedSize: string | null;
  /** The measuring guide shows leg/compression photos — suppress it for braces. */
  showMeasuringGuideLink?: boolean;
}

export function SizeFinder({ sizingChart, availableSizes, onSelectSize, selectedSize, showMeasuringGuideLink = true }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [measurements, setMeasurements] = useState<Record<string, string>>(() => {
    const saved = loadMeasurements();
    const init: Record<string, string> = {};
    for (const row of sizingChart.rows) {
      const key = canonicalKey(row.measurement);
      if (saved[key] !== undefined) init[key] = String(saved[key]);
    }
    return init;
  });

  // If we have saved measurements, auto-expand so the user sees their suggestion
  useEffect(() => {
    if (Object.keys(measurements).length > 0) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numericMeasurements = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(measurements)) {
      const n = parseFloat(v);
      if (!isNaN(n) && n > 0) out[k] = n;
    }
    return out;
  }, [measurements]);

  // Persist every change so other products benefit next time
  useEffect(() => {
    if (Object.keys(numericMeasurements).length > 0) saveMeasurements(numericMeasurements);
  }, [numericMeasurements]);

  const recommendation = useMemo(() => {
    if (Object.keys(numericMeasurements).length === 0) return null;

    const scores = sizingChart.columns.map((size, colIdx) => {
      let matches = 0;
      let mismatches = 0;
      for (const row of sizingChart.rows) {
        const key = canonicalKey(row.measurement);
        const value = numericMeasurements[key];
        if (value === undefined) continue;
        const range = parseRange(row.values[colIdx]);
        if (!range) continue;
        if (inRange(value, range)) matches++;
        else mismatches++;
      }
      return { size, matches, mismatches };
    });

    const available = availableSizes.length > 0
      ? scores.filter((s) => availableSizes.includes(s.size))
      : scores;

    const clean = available.filter((s) => s.mismatches === 0 && s.matches > 0);
    if (clean.length === 0) {
      return { kind: 'no_fit' as const };
    }
    clean.sort((a, b) => b.matches - a.matches);
    const best = clean[0];
    const filled = Object.keys(numericMeasurements).length;
    return {
      kind: 'fit' as const,
      size: best.size,
      matches: best.matches,
      complete: best.matches === filled && best.matches === sizingChart.rows.length,
    };
  }, [numericMeasurements, sizingChart, availableSizes]);

  const handleApply = useCallback(() => {
    if (recommendation && recommendation.kind === 'fit') {
      onSelectSize(recommendation.size);
      toast.success(`Size ${recommendation.size} applied`, { icon: '\u{1F3AF}' });
    }
  }, [recommendation, onSelectSize]);

  const handleChange = (key: string, value: string) => {
    if (value === '' || /^\d+(\.\d*)?$/.test(value)) {
      setMeasurements((prev) => ({ ...prev, [key]: value }));
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe] border border-[#7B68CC]/20 rounded-2xl overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#7B68CC]/5 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#7B68CC] flex items-center justify-center flex-shrink-0">
            <Ruler size={14} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-ndms-navy leading-tight">Find My Size</p>
            <p className="text-[11px] text-gray-500 leading-tight">Enter your measurements — we'll pick the right fit</p>
          </div>
        </div>
        <ChevronDown size={16} className={`text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3 border-t border-[#7B68CC]/15 pt-3">
              {sizingChart.rows.map((row) => {
                const key = canonicalKey(row.measurement);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-700 flex-1 min-w-0">
                      {row.measurement}
                    </label>
                    <div className="relative flex-shrink-0">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={measurements[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder="0"
                        className="w-20 pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-[#7B68CC] focus:ring-1 focus:ring-[#7B68CC]/30 transition-all bg-white"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 font-medium">
                        {unitSuffix(row.unit)}
                      </span>
                    </div>
                  </div>
                );
              })}

              {showMeasuringGuideLink && (
                <button
                  onClick={() => setShowGuide(true)}
                  className="text-[11px] text-[#7B68CC] font-semibold hover:underline flex items-center gap-1"
                >
                  <Ruler size={11} /> Not sure where to measure? See the guide
                </button>
              )}

              <AnimatePresence mode="wait">
                {recommendation && recommendation.kind === 'fit' && (
                  <motion.div
                    key={`fit-${recommendation.size}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="bg-white rounded-xl p-3 border border-[#7B68CC]/30 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-[#7B68CC] fill-[#7B68CC]/20" />
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider leading-tight">
                            {recommendation.complete ? 'Best fit' : 'Recommended'}
                          </p>
                          <p className="text-base font-bold text-ndms-navy leading-tight">
                            Size {recommendation.size}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleApply}
                        disabled={selectedSize === recommendation.size}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          selectedSize === recommendation.size
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : 'bg-[#7B68CC] text-white hover:bg-[#6a55b8] shadow-sm hover:shadow'
                        }`}
                      >
                        {selectedSize === recommendation.size ? (
                          <span className="flex items-center gap-1"><Check size={12} /> Applied</span>
                        ) : 'Use this size'}
                      </button>
                    </div>
                  </motion.div>
                )}

                {recommendation && recommendation.kind === 'no_fit' && (
                  <motion.div
                    key="no_fit"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="bg-amber-50 rounded-xl p-3 border border-amber-200"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-amber-900">Outside standard range</p>
                        <p className="text-[11px] text-amber-700 leading-snug mt-0.5">
                          Your measurements don't fit our standard sizes. Call{' '}
                          <a href="tel:+13069794115" className="underline font-semibold">306-979-4115</a>{' '}
                          for custom sizing.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMeasuringGuideLink && showGuide && <MeasuringGuideModal onClose={() => setShowGuide(false)} />}
      </AnimatePresence>
    </div>
  );
}
